// src/components/property/PropertyStatisticsView.js
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { apiFetch } from '../../utils/apiClient';
import { BASE_URL } from '../../utils/config';
import PropertyStatsTable from './PropertyStatsTable';
import './PropertyStatisticsView.css'; // New CSS file

const PropertyStatisticsView = ({ searchCriteria = {}, onPropertySelect, scrollContainerRef }) => {
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const [sortConfig, setSortConfig] = useState({ key: 'propertyID', direction: 'ascending' });
  const isInitialSort = useRef(true); // To prevent scrolling on initial load

  const finalQueryString = useMemo(() => {
    const filterEntries = Object.entries(searchCriteria)
      .map(([key, value]) => {
        if (value === undefined || value === null || String(value).trim() === '') return null;
        if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
          return Object.entries(value)
            .map(([subKey, subValue]) => {
              if (subValue === undefined || subValue === null || String(subValue).trim() === '') return null;
              return `${encodeURIComponent(key)}[${encodeURIComponent(subKey)}]=${encodeURIComponent(subValue)}`;
            })
            .filter(Boolean)
            .join('&');
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      })
      .filter(Boolean);

    const forMapParam = 'forMap=true';
    let queryString = filterEntries.join('&');
    return queryString ? `${queryString}&${forMapParam}` : forMapParam;
  }, [searchCriteria]);

  useEffect(() => {
    const fetchPropertiesForStats = async () => {
      if (!user.token) {
        setIsLoading(false);
        setError('Authentication required to view statistics.');
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const url = `${BASE_URL}/api/properties/my-properties?${finalQueryString}`;

        const response = await apiFetch(url, {}, user.token);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to fetch property statistics.');
        }
        const data = await response.json();
        setProperties(data.properties || []);
      } catch (err) {
        setError(err.message);
        addNotification(err.message, 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPropertiesForStats();
  }, [finalQueryString, user.token, addNotification]);

  const sortedProperties = useMemo(() => {
    let sortableItems = [...properties];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        const getSortValue = (item, key) => {
          if (key === 'views') return item.stats?.viewCount || 0;
          if (key === 'reports') return item.stats?.reportCount || 0;
          if (key === 'saves') return item.stats?.saveCount || 0;
          if (key === 'inquiries') return item.stats?.inquiryCount || 0;
          if (key === 'address') return `${item.address || ''}, ${item.city || ''}`.toLowerCase();
          const value = item[key];
          return typeof value === 'string' ? value.toLowerCase() : value;
        };

        const aValue = getSortValue(a, sortConfig.key);
        const bValue = getSortValue(b, sortConfig.key);

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [properties, sortConfig]);

  // This effect runs AFTER the component re-renders due to a sortConfig change
  useEffect(() => {
    // We don't want to scroll on the initial render, only on subsequent sorts.
    if (isInitialSort.current) {
      isInitialSort.current = false;
      return;
    }
    if (scrollContainerRef && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [sortConfig, scrollContainerRef]); // This effect depends on sortConfig

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  if (isLoading) return <div className="stats-view-loading">Loading statistics...</div>;
  if (error) return <div className="stats-view-error">Error: {error}</div>;

  return <div className="property-statistics-view">{<PropertyStatsTable properties={sortedProperties} onPropertySelect={onPropertySelect} requestSort={requestSort} sortConfig={sortConfig} scrollContainerRef={scrollContainerRef} />}</div>;
};

export default PropertyStatisticsView;
